import { MouseEventHandler, ReactElement } from 'react'
import { ButtonBase, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { Typography } from '../../text'

type TextButtonVariants = 'primary' | 'destructive' | 'secondary'

export type TextButtonProps = {
    label: string
    icon?: ReactElement
    variant?: TextButtonVariants
    onClick: MouseEventHandler<HTMLButtonElement>
    disabled?: boolean
}

export function TextButton({ label, icon, variant = 'primary', onClick, disabled }: TextButtonProps) {
    return (
        <StyledButtonBase $variant={variant} onClick={onClick} disableRipple disabled={disabled}>
            {icon && (
                <IconContainer $disabled={disabled} $variant={variant}>
                    {icon}
                </IconContainer>
            )}
            <Typography variant="linkCaps" noWrap>
                {label}
            </Typography>
        </StyledButtonBase>
    )
}

const IconContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $variant: TextButtonVariants; $disabled?: boolean }>(({ $variant, $disabled }) => ({
    fontSize: tokens.SizingIconM,
    color: $disabled ? tokens.ColorIconDisabled : $variant === 'secondary' ? tokens.ColorIconBoldest : tokens.ColorIconInverse,
}))

const StyledButtonBase = styled(ButtonBase, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $variant: TextButtonVariants }>(({ $variant }: { $variant: keyof typeof TEXT_BUTTON_VARIANTS }) => ({
    lineHeight: 0,
    height: '40px',
    paddingLeft: tokens.SizingSpacing200,
    paddingRight: tokens.SizingSpacing300,
    borderRadius: tokens.SizingCornerS,
    ...TEXT_BUTTON_VARIANTS[$variant].default,
    '& > .MuiTypography-root': {
        paddingLeft: tokens.SizingSpacing100,
    },
    '&:hover': {
        ...TEXT_BUTTON_VARIANTS[$variant].hover,
    },
    '&:active': {
        ...TEXT_BUTTON_VARIANTS[$variant].pressed,
    },
    '&.Mui-disabled': {
        color: tokens.ColorTextDisabled,
        backgroundColor: tokens.ColorSurfaceDisabled,
        boxShadow: 'none',
    },
}))

const TEXT_BUTTON_VARIANTS = {
    primary: {
        default: {
            backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
            color: tokens.ColorTextInverse,
        },
        hover: { backgroundColor: tokens.ColorSurfacePositiveBoldHover, color: tokens.ColorTextInverse },
        pressed: { backgroundColor: tokens.ColorSurfacePositiveBoldPressed, color: tokens.ColorTextInverse },
    },
    destructive: {
        default: {
            backgroundColor: tokens.ColorSurfaceDangerBoldDefault,
            color: tokens.ColorTextInverse,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceDangerBoldHover,
            color: tokens.ColorTextInverse,
        },
        pressed: {
            backgroundColor: tokens.ColorSurfaceDangerBoldPressed,
            color: tokens.ColorTextInverse,
        },
    },
    secondary: {
        default: {
            backgroundColor: tokens.ColorSurfaceDefault,
            color: tokens.ColorTextBoldest,
            boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderDefault}`,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceHover,
            color: tokens.ColorTextBoldest,
            boxShadow: 'none',
        },
        pressed: {
            backgroundColor: tokens.ColorSurfacePressed,
            color: tokens.ColorTextBoldest,
            boxShadow: 'none',
        },
    },
}
